/* eslint-disable max-lines */
import { isNil, orderBy } from 'lodash';

import numOrDefault from 'lib/helpers/numOrDefault';
import { ia, iaRa, inRn } from 'lib/helpers/utility';

import { amountToCents } from 'components/practice/BeyondBilling/Claims/CMS1500/lib/amountToCents';
import { calcLatestBenefits } from 'components/practice/BeyondBilling/ElectronicInvoicing/lib/calcLatestBenefits';
import {
  modifyPackages,
  modifyProducts
} from 'components/practice/BeyondBilling/ElectronicInvoicing/lib/internal/configs';
import {
  denied_codes,
  patient_responsibility
} from 'components/practice/BeyondBilling/lib/insuranceAdjustmentGroups';
import { MAX_PERCENT_VALUE } from 'components/shared/CurrencyInput/lib/constants';

const INS_TYPES = ['primary', 'secondary', 'tertiary'];

const calculateAdjustmentsFromLast = (adjustments) => {
  if (!ia(adjustments)) return { ins_payment: 0, pt_adjustment: 0 };

  const lastAdjustment = adjustments[adjustments.length - 1] || {};
  const ins_payment = Math.round(lastAdjustment?.paid ?? 0) || 0;
  let pt_adjustment = 0;
  let ins_balance = 0;

  if (ia(lastAdjustment?.pt_adj)) {
    for (const { group, code, amount } of lastAdjustment.pt_adj) {
      if (lastAdjustment?.patient_responsibility === false) {
        if (!(group === 'PR' && +code === 45)) {
          pt_adjustment += amountToCents(amount);
        }
      } else {
        pt_adjustment += amountToCents(amount);
      }
    }
  }

  if (ia(lastAdjustment?.ins_adj)) {
    for (const { group, code, amount, type = '' } of lastAdjustment.ins_adj) {
      const hasPRCode = patient_responsibility[group]?.includes(+code) || type == 'pt_rep';
      const hasDeniedCode = denied_codes[group]?.includes(code);

      if (type == 'ins_balance') ins_balance += amountToCents(amount);

      if (lastAdjustment?.patient_responsibility === false) {
        if (!(group === 'PR' && +code === 45)) {
          if (group === 'PR' || hasPRCode || hasDeniedCode) {
            pt_adjustment += amountToCents(amount);
          }
        }
      } else {
        if (group === 'PR' || hasPRCode || hasDeniedCode) {
          pt_adjustment += amountToCents(amount);
        }
      }
    }
  }

  return { ins_payment, pt_adjustment, ins_balance };
};

const primaryCalculation = (procedure) => {
  let charge = 0;
  let ins_payment = 0;
  let ins_adjustment = 0;
  let pt_adjustment = 0;
  let ins_balance = 0;
  const other_adj = procedure?.other_adj ?? 0;

  charge += ['self_pay', 'cash'].includes(procedure.type)
    ? (procedure.pt_balance ?? +procedure?.total_cost_cents ?? 0)
    : (procedure.charge ?? 0);

  for (const insType of INS_TYPES) {
    const adjustments = procedure?.[insType];
    if (!adjustments) continue;

    const {
      ins_payment: insPay,
      pt_adjustment: ptAdj,
      ins_balance: insBalance
    } = calculateAdjustmentsFromLast(adjustments);

    ins_payment += insPay;
    pt_adjustment += ptAdj;
    ins_balance += insBalance;
  }

  let insAdj =
    numOrDefault(charge) -
    numOrDefault(ins_payment) -
    numOrDefault(calcLatestBenefits({ procedure })?.ptBalance) -
    numOrDefault(other_adj) -
    numOrDefault(ins_balance);

  if (['product', 'package', 'self_pay'].includes(procedure.type) || procedure?.denied) {
    insAdj = 0;
  }

  ins_adjustment += insAdj;

  return {
    ...procedure,
    charge,
    ins_adjustment,
    pt_adjustment,
    ins_payment,
    other_adj,
    ins_balance
  };
};

const secondCalculation = (procedure) => {
  const { ins_payment = 0, pt_balance = 0 } = procedure;
  const final_ins_payment = ins_payment - pt_balance;
  return Math.max(final_ins_payment, 0);
};

const calculateProceduresTotal = (procedures = []) => {
  return procedures?.reduce(
    (acc, procedure) => {
      const qty =
        (inRn(procedure?.quantity) ?? inRn(procedure?.units) ?? inRn(procedure?.sales_count)) || 1;

      const qtyToMultiply =
        !procedure?.type || procedure?.type?.toLowerCase() === 'product' ? 1 : qty;

      let result = {};

      if (Object.keys(procedure).includes('primary')) {
        result = primaryCalculation(procedure);

        acc.local_tax += numOrDefault(result.local_tax_rate * qtyToMultiply);
        acc.state_tax += numOrDefault(result.state_tax_rate * qtyToMultiply);
        acc.charge += numOrDefault(result.charge * qtyToMultiply);
        acc.ins_adjustment += numOrDefault(result.ins_adjustment * qtyToMultiply);
        acc.ins_payment += numOrDefault(result.ins_payment * qtyToMultiply);
        acc.pt_adjustment += numOrDefault(result.pt_adjustment);
        acc.other_adj += numOrDefault(result.other_adj);
        acc.ins_balance += numOrDefault(result.ins_balance);
      } else if (
        procedure?.ins_payment > 0 &&
        procedure?.ins_adjustment > 0 &&
        procedure?.charge === procedure?.ins_payment + procedure?.ins_adjustment
      ) {
        const ins_payment = secondCalculation(procedure);
        const ins_adjustment = Math.max(
          0,
          numOrDefault(procedure.ins_adjustment * qtyToMultiply) - numOrDefault(procedure.other_adj)
        );
        acc.local_tax += numOrDefault(procedure.local_tax_rate * qtyToMultiply);
        acc.state_tax += numOrDefault(procedure.state_tax_rate * qtyToMultiply);
        acc.charge += (procedure.charge ?? procedure.pt_balance ?? 0) * qtyToMultiply;
        acc.ins_adjustment += ins_adjustment;
        acc.ins_payment += numOrDefault(ins_payment * qtyToMultiply);
        acc.pt_adjustment += numOrDefault(procedure.pt_adjustment);
        acc.other_adj += numOrDefault(procedure.other_adj);
      } else if (procedure?.status !== 'cancelled') {
        let {
          charge = 0,
          total_amount_cents = 0,
          pt_balance,
          total_cost_cents = 0,
          charge_type,
          ins_payment,
          ins_adjustment,
          other_adj = 0,
          type,
          denied = false
        } = procedure;

        let insAdj = 0;

        if (type !== 'primary') {
          insAdj = Math.max(charge - pt_balance, 0);
        }

        if (['product', 'package', 'self_pay', 'cash', 'unapplied'].includes(type) || denied) {
          insAdj = 0;
          charge = 0;
        }

        if (
          isNil(pt_balance) &&
          charge_type === 'copay' &&
          isNil(ins_payment) &&
          isNil(ins_adjustment)
        ) {
          insAdj = charge;
        }

        acc.local_tax += numOrDefault(procedure.local_tax_rate * qtyToMultiply);
        acc.state_tax += numOrDefault(procedure.state_tax_rate * qtyToMultiply);
        acc.charge +=
          ((+charge || pt_balance) ?? (+total_cost_cents || +total_amount_cents)) * qtyToMultiply;
        acc.ins_adjustment += Math.max(
          0,
          numOrDefault(insAdj * qtyToMultiply) - numOrDefault(other_adj)
        );
        acc.pt_adjustment += numOrDefault(pt_balance);
        acc.other_adj += numOrDefault(other_adj);
      }

      acc.qty += qty;
      return acc;
    },
    {
      charge: 0,
      ins_adjustment: 0,
      pt_adjustment: 0,
      ins_payment: 0,
      other_adj: 0,
      qty: 0,
      local_tax: 0,
      state_tax: 0,
      ins_balance: 0
    }
  );
};

export const getProceduresTotal = ({ invoices = [], show = false, isAEOB = true }) => {
  const filteredInvoices = invoices.filter((invoice) => invoice.show || show);
  const procedures = filteredInvoices
    .map((invoice) => iaRa(invoice.procedures).filter((p) => p.show || show))
    .flat();
  const products = filteredInvoices
    .map((invoice) =>
      modifyProducts({
        products: iaRa(invoice.products).filter((p) => p.show || show),
        isTotal: true
      })
    )
    .flat();

  const packages = filteredInvoices
    .map((invoice) =>
      modifyPackages({
        packages: iaRa(invoice.packages).filter((p) => p.show || show),
        isTotal: true
      })
    )
    .flat();

  const services = [...procedures, ...products, ...packages];

  const itemTotals = calculateProceduresTotal(services);

  const invoiceTotals = filteredInvoices.reduce(
    (acc, invoice) => ({
      totalAmount: acc.totalAmount + (invoice?.total_amount ?? 0),
      discount: acc.discount + (invoice?.discount ?? 0),
      write_off: acc.write_off + (invoice?.write_off ?? 0),
      tax: acc.tax + (invoice?.tax ?? 0),
      amount_paid: acc.amount_paid + (invoice?.amount_paid ?? 0),
      ptBalance: acc.ptBalance + (invoice?.invoice_balance ?? invoice?.balance ?? 0)
    }),
    {
      amount_paid: 0,
      discount: 0,
      tax: 0,
      write_off: 0,
      ptBalance: 0,
      totalAmount: 0
    }
  );

  const { charge, ins_payment, ins_adjustment, other_adj, ins_balance } = itemTotals || {};
  const { write_off, discount, tax, amount_paid, ptBalance, totalAmount } = invoiceTotals || {};

  let subTotal = charge;
  let total =
    charge -
    ins_payment -
    ins_adjustment -
    discount -
    amount_paid +
    tax -
    write_off -
    other_adj -
    ins_balance;

  if (!isAEOB) {
    total = other_adj ? charge - other_adj : ptBalance;
    subTotal = totalAmount;

    invoiceTotals.ins_payment = 0;
    invoiceTotals.ins_adjustment = 0;
  }

  return {
    ...itemTotals,
    ...invoiceTotals,
    total,
    subTotal
  };
};

export const getInvoicesDetails = (invoices = []) =>
  orderBy(invoices, ['dateofservice'], 'desc').map((invoice) => ({
    id: invoice.id,
    dateOfService: invoice.dateofservice,
    discount: invoice?.discount ?? 0,
    procedures: invoice.procedures,
    products: invoice.products,
    packages: invoice.packages,
    tax: invoice.tax ?? 0,
    amount_paid: invoice.amount_paid ?? 0,
    write_off: invoice.write_off ?? 0,
    created_at: invoice.created_at
  }));

export const calculateAmountFromPercentage = (percentage, baseTotal) =>
  baseTotal > 0 ? Math.round((percentage / MAX_PERCENT_VALUE) * baseTotal) : 0;

export const calculatePercentageFromAmount = (amountCents, baseTotal) =>
  baseTotal > 0 ? Math.round((amountCents / baseTotal) * MAX_PERCENT_VALUE) : 0;
